import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchPageBySlug, getLocalizedValue } from '../lib/cms';
import usePageMetadata from '../hooks/usePageMetadata';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

export default function ServiceData() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    async function loadData() {
      try {
        const page = await fetchPageBySlug('data-migration');
        setPageData(page);
      } catch (err) {
        console.error('Error loading Data Migration page:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Set page metadata
  const title = pageData ? getLocalizedValue(pageData.meta_title, language) : 'Data Migration Manager - Insightly ApS';
  const description = pageData ? getLocalizedValue(pageData.meta_description, language) : 'Sikker datamigrering og master data management til komplekse ERP-projekter. Læs om Renés skalerbare migreringsframeworks.';
  usePageMetadata(title, description);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-offwhite">
        <div className="flex flex-col items-center gap-3">
          <i className="fa-solid fa-spinner animate-spin text-3xl text-petrol"></i>
          <p className="text-sm text-navy-900/60 font-medium">Henter indhold...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-offwhite py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Editorial Header */}
        <div className="max-w-3xl mb-16 space-y-4">
          <span className="text-xs uppercase tracking-[0.25em] text-petrol font-bold">// {language === 'da' ? 'YDELSE · DATA GOVERNANCE' : language === 'en' ? 'SERVICE · DATA GOVERNANCE' : 'DIENSTLEISTUNG · DATA GOVERNANCE'}</span>
          <h1 className="text-4xl sm:text-5xl font-serif font-medium text-navy-900 leading-tight">
            {pageData ? getLocalizedValue(pageData.title, language) : t.home.dataTitle}
          </h1>
          <p className="text-lg text-navy-900/60 font-sans">
            {t.services.dataSubtitle}
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column - Image & Quick Facts */}
          <div className="lg:col-span-5 space-y-8">
            <div className="relative rounded-none overflow-hidden shadow-2xl border border-bluegray">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8297297f-eccf-4eba-a924-6f973a63cdc4/data-migration.jpg"
                alt="Datamigrering og Analyse"
                className="w-full h-[350px] object-cover rounded-none"
              />
            </div>

            {/* Quick Facts Card */}
            <div className="bg-white rounded-none border border-bluegray p-6 sm:p-8 shadow-xl space-y-6">
              <h3 className="font-serif text-lg font-bold text-navy-900">{language === 'da' ? 'Sikker overgang til nyt ERP' : language === 'en' ? 'Secure transition to new ERP' : 'Sicherer Übergang zum neuen ERP'}</h3>
              <ul className="space-y-4 text-sm text-charcoal/80">
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-petrol mt-0.5 shrink-0"></i>
                  <span><strong>{language === 'da' ? 'Skalerbare frameworks:' : language === 'en' ? 'Scalable frameworks:' : 'Skalierbare Frameworks:'}</strong> {language === 'da' ? 'René designer genanvendelige og robuste migreringsflows.' : language === 'en' ? 'René designs reusable and robust migration flows.' : 'René entwirft wiederverwendbare und robuste Migrationsabläufe.'}</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-petrol mt-0.5 shrink-0"></i>
                  <span><strong>{language === 'da' ? 'Datakvalitet i fokus:' : language === 'en' ? 'Data quality in focus:' : 'Datenqualität im Fokus:'}</strong> {language === 'da' ? 'Sikrer, at data renses, valideres og beriges inden go-live.' : language === 'en' ? 'Ensures that data is cleansed, validated, and enriched before go-live.' : 'Stellt sicher, dass Daten vor dem Go-Live bereinigt, validiert und angereichert werden.'}</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-petrol mt-0.5 shrink-0"></i>
                  <span><strong>{language === 'da' ? 'Minimal nedetid:' : language === 'en' ? 'Minimal downtime:' : 'Minimale Ausfallzeit:'}</strong> {language === 'da' ? 'Tæt planlægning af cutover-aktiviteter sikrer uforstyrret drift.' : language === 'en' ? 'Close planning of cutover activities ensures uninterrupted operations.' : 'Enge Planung von Cutover-Aktivitäten sorgt für ungestörten Betrieb.'}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Right Column - Detailed Text */}
          <div className="lg:col-span-7 space-y-8 bg-white rounded-none border border-bluegray p-8 sm:p-10 shadow-xl">
            <div 
              className="prose prose-navy max-w-none text-charcoal/80 leading-relaxed space-y-6 font-sans text-base"
              dangerouslySetInnerHTML={{ __html: pageData ? getLocalizedValue(pageData.content, language) : '' }}
            />

            {/* Call to Action */}
            <div className="pt-8 border-t border-bluegray/60 flex flex-col sm:flex-row items-center justify-between gap-6">
              <div>
                <h4 className="font-serif text-lg font-bold text-navy-900">{language === 'da' ? 'Står I overfor en kompleks datamigrering?' : language === 'en' ? 'Are you facing a complex data migration?' : 'Stehen Sie vor einer komplexen Datenmigration?'}</h4>
                <p className="text-sm text-charcoal/60 mt-1">{language === 'da' ? 'Lad os drøfte jeres datastrategi og sikre en tryg go-live.' : language === 'en' ? 'Let\'s discuss your data strategy and ensure a safe go-live.' : 'Lassen Sie uns Ihre Datenstrategie besprechen und einen sicheren Go-Live gewährleisten.'}</p>
              </div>
              <Link
                to="/kontakt"
                className="bg-navy-900 hover:bg-petrol text-offwhite px-6 py-3.5 rounded-none text-xs font-bold uppercase tracking-wider transition-all duration-300 shadow-md whitespace-nowrap"
              >
                {t.nav.contact}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
